// The Tuya Testing bench's state — everything except layout.
//
// Both shells mount this. The mobile screen stacks the cards and the desktop
// page puts them in a grid, but the list, the subscriptions, the parsing and the
// commands are all here, so the two cannot answer differently about a device.
//
// ── One listener, not one per card ─────────────────────────────────────────
// The Dart registers a per-device callback inside the list builder, which is
// re-run on every rebuild. Here a single `addMessageListener` fans out by unique
// number: the subscriptions are ref-counted by mqttClient, and a card
// re-rendering cannot leak another handler.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  subscribeTopic, unsubscribeTopic, addMessageListener,
} from '../mqtt/mqttClient';
import { publishCommand } from '../utils/deviceController';
import { unlockCommand } from '../devices/deviceMeta';
import {
  getTuyaDeviceTypes, getTuyaDeviceStatuses, setTuyaDeviceType,
  parseTuyaStatus, classifyTuyaMessage, tuyaKind, tuyaFanCommandValue,
  lastSeenFromRow, seenStateOf, SEEN_LIVE,
} from '../devices/tuyaTesting';

export const TUYA_LIMITS = [10, 25, 50, 100];

// How often the ages re-compute. One second, because the thing being watched is
// a device going quiet and the operator is looking at the screen while it
// happens — a five-second tick would put the drop up to five seconds late,
// which on a bench is the difference between "it dropped when I unplugged the
// router" and "it dropped at some point".
export const TICK_MS = 1000;

// Drop/recover entries kept. Long enough to review a session, bounded so a bench
// left running overnight does not grow without limit.
export const MAX_DROP_EVENTS = 200;

export function useTuyaBench({ userId, limit = 10, deviceTypeFilter = 0 }) {
  const [rows, setRows] = useState(null); // null = first load not finished
  const [types, setTypes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // uno -> string[] of channel values, and uno -> last raw payload seen.
  const [values, setValues] = useState({});
  const [messages, setMessages] = useState({});

  // uno -> epoch ms of the last sighting. Seeded from the server's stamp, then
  // advanced by ANY frame on the device's topic — a heartbeat, a status, an OTA
  // line. All of them prove the same thing: it is there.
  const [lastSeen, setLastSeen] = useState({});

  // The whole point of the liveness column: a device crossing from live to
  // quiet, with the clock time it happened. Newest first.
  const [dropEvents, setDropEvents] = useState([]);

  // `now` is state, not Date.now() read during render, so the ages actually
  // re-render as time passes rather than only when a frame arrives.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(t);
  }, []);

  // Read inside the MQTT listener, which is registered once — so the current
  // row set has to be reachable without re-registering it.
  const rowsRef = useRef([]);
  rowsRef.current = rows || [];

  // uno -> was it live at the previous tick. The transition, not the state, is
  // what gets logged.
  const wasLiveRef = useRef(new Map());

  // ── The list ─────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTuyaDeviceStatuses(limit, deviceTypeFilter);
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
      setError(null);
      // Seed the tiles from the stored Status. It may be stale — that is what
      // the live subscription is for — but an empty card would be a worse lie
      // than an old one, because it reads as "device has no channels".
      const seeded = {};
      const seen = {};
      list.forEach((r) => {
        seeded[String(r.UniqueNumber)] = parseTuyaStatus(r.Status, r.DeviceID);
        seen[String(r.UniqueNumber)] = lastSeenFromRow(r);
      });
      setValues(seeded);
      setLastSeen(seen);
      setMessages({});

      // Seed the transition tracker WITHOUT emitting. A device that was already
      // quiet when the list loaded did not drop while we were watching, and
      // logging it as a drop at load time would put a false timestamp — the
      // moment the page opened — on an event that happened who knows when.
      wasLiveRef.current = new Map(
        list.map((r) => {
          const age = seen[String(r.UniqueNumber)] == null
            ? null : Date.now() - seen[String(r.UniqueNumber)];
          return [String(r.UniqueNumber), seenStateOf(age) === SEEN_LIVE];
        }),
      );
    } catch (e) {
      setError(e?.message || 'Could not reach the server.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [limit, deviceTypeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getTuyaDeviceTypes()
      .then((res) => setTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTypes([]));
  }, []);

  // ── Live traffic ─────────────────────────────────────────────────────────
  const unos = useMemo(
    () => (rows || []).map((r) => String(r.UniqueNumber)),
    [rows],
  );
  // Joined, so the effect re-runs when the SET changes rather than on every
  // re-render that hands it a fresh array with the same contents.
  const unoKey = unos.join(',');

  // A boot banner asks for a fresh list, but a rack of boards coming up together
  // would fire one reload each. Coalesced into a single delayed refresh.
  const reloadTimer = useRef(null);
  useEffect(() => () => clearTimeout(reloadTimer.current), []);

  useEffect(() => {
    if (!unos.length) return undefined;
    unos.forEach((u) => subscribeTopic(`${u}/client`));
    const off = addMessageListener((topic, payload) => {
      const uno = String(topic).split('/')[0];
      if (!rowsRef.current.some((r) => String(r.UniqueNumber) === uno)) return;
      // Stamped BEFORE the message is classified, and on every frame including
      // the ones the classifier throws away. A heartbeat carries no channel
      // values and would otherwise not count as a sighting — which is exactly
      // backwards, since the heartbeat is the thing that proves it is there.
      setLastSeen((s) => ({ ...s, [uno]: Date.now() }));

      const msg = classifyTuyaMessage(payload);
      if (!msg) return;

      setMessages((m) => ({ ...m, [uno]: msg.text }));
      if (msg.kind === 'status') {
        setValues((v) => ({ ...v, [uno]: msg.values }));
      } else if (msg.kind === 'started') {
        clearTimeout(reloadTimer.current);
        reloadTimer.current = setTimeout(() => load(), 1500);
      }
    });
    return () => {
      off();
      unos.forEach((u) => unsubscribeTopic(`${u}/client`));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unoKey, load]);

  // ── Ages, and the moment one crosses ─────────────────────────────────────
  //
  // Recomputed on the tick, so a device that simply stops talking is noticed
  // without anything arriving to notice it with. That is the case that matters:
  // going offline is the ABSENCE of a message, and nothing else on the screen is
  // driven by an absence.
  const liveness = useMemo(() => {
    const out = {};
    (rows || []).forEach((r) => {
      const uno = String(r.UniqueNumber);
      const at = lastSeen[uno];
      const age = at == null ? null : Math.max(0, now - at);
      out[uno] = { at: at ?? null, age, state: seenStateOf(age) };
    });
    return out;
  }, [rows, lastSeen, now]);

  useEffect(() => {
    const events = [];
    Object.entries(liveness).forEach(([uno, l]) => {
      const isLive = l.state === SEEN_LIVE;
      const was = wasLiveRef.current.get(uno);
      // `undefined` is a device we have not classified yet — record its state,
      // emit nothing. Only a genuine true->false or false->true is an event.
      if (was !== undefined && was !== isLive) {
        events.push(isLive
          ? { uno, kind: 'back', at: Date.now(), downFor: null }
          // The drop happened one beat before we could see it: the device went
          // quiet at its LAST sighting, not at the moment the threshold expired.
          // Reporting the threshold time would be TUYA_LIVE_MS too late every
          // time — which on a bench is the one number being measured.
          : { uno, kind: 'drop', at: l.at ?? Date.now(), noticedAt: Date.now() });
      }
      wasLiveRef.current.set(uno, isLive);
    });
    if (!events.length) return;
    // Fill in how long each recovered device was down, from its own drop.
    setDropEvents((prev) => {
      const withDuration = events.map((e) => {
        if (e.kind !== 'back') return e;
        const drop = prev.find((p) => p.uno === e.uno && p.kind === 'drop');
        return { ...e, downFor: drop ? e.at - drop.at : null };
      });
      return [...withDuration, ...prev].slice(0, MAX_DROP_EVENTS);
    });
  }, [liveness]);

  const clearDropEvents = useCallback(() => setDropEvents([]), []);

  const counts = useMemo(() => {
    const out = { live: 0, late: 0, gone: 0, never: 0 };
    Object.values(liveness).forEach((l) => { out[l.state] += 1; });
    return out;
  }, [liveness]);

  // ── Commands ─────────────────────────────────────────────────────────────
  //
  // Every one of these goes out as `[uno]<msg>#<userId>` — publishCommand builds
  // the envelope, so the bench speaks exactly what the room screens speak.
  //
  // ⚠️ NOTHING HERE IS OPTIMISTIC. The tile moves when the device says it moved
  // and not before: a bench that painted the relay on regardless would hide the
  // single failure it exists to find.
  const send = useCallback(
    (uno, message) => publishCommand(uno, message, userId),
    [userId],
  );

  const toggleChannel = useCallback((uno, index) => {
    const current = Number((values[String(uno)] || [])[index]);
    const next = Number.isFinite(current) && current !== 0 ? 0 : 1;
    send(uno, `${index + 1}-${next}`);
  }, [values, send]);

  const setFanSpeed = useCallback((uno, sliderValue) => {
    send(uno, `1-${tuyaFanCommandValue(sliderValue)}`);
    // The fan is the one exception to "never optimistic", and it is the Dart's
    // exception too: the slider is a drag, so leaving it pinned to the reported
    // value until the device answers makes it fight the finger holding it.
    setValues((v) => ({ ...v, [String(uno)]: [String(sliderValue)] }));
  }, [send]);

  const allOn = useCallback((uno) => send(uno, 'allon'), [send]);
  const allOff = useCallback((uno) => send(uno, 'alloff'), [send]);
  // Door locks: the unlock word depends on the type (3 OPENDOOR, 102 LOCKUNLOCK).
  const unlock = useCallback((uno, deviceId) => send(uno, unlockCommand(deviceId)), [send]);
  const deviceDetails = useCallback((uno) => send(uno, 's05'), [send]);
  const updateCode = useCallback((uno) => send(uno, 'updatecode'), [send]);

  const allOnEverything = useCallback(
    () => (rows || []).forEach((r) => allOn(r.UniqueNumber)),
    [rows, allOn],
  );
  const allOffEverything = useCallback(
    () => (rows || []).forEach((r) => allOff(r.UniqueNumber)),
    [rows, allOff],
  );

  /**
   * Correct a device's DeviceType: write the install row, then restart it.
   *
   * The restart is not optional — the firmware reads its type at boot, so
   * without it the device keeps reporting in the old shape and the card that
   * was just fixed goes wrong again on the next frame.
   */
  const changeType = useCallback(async (uno, deviceId) => {
    await setTuyaDeviceType(uno, deviceId);
    send(uno, 'restartwifimodule');
    setRows((list) => (list || []).map((r) => (
      String(r.UniqueNumber) === String(uno)
        ? { ...r, DeviceID: Number(deviceId) }
        : r
    )));
  }, [send]);

  return {
    unlock,
    rows,
    types,
    values,
    messages,
    liveness,
    counts,
    dropEvents,
    clearDropEvents,
    now,
    loading,
    error,
    reload: load,
    kindOf: tuyaKind,
    toggleChannel,
    setFanSpeed,
    allOn,
    allOff,
    deviceDetails,
    updateCode,
    allOnEverything,
    allOffEverything,
    changeType,
  };
}

export default useTuyaBench;
